import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Play, CheckCircle, ChevronDown, ChevronUp,
  RefreshCw, AlertTriangle, Users, Eye, Calendar, Search,
  Upload, Download, FileSpreadsheet, X
} from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import { apiBaseUrl } from "services/config";
import { useTheme } from "context/ThemeContext";

const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const fmt    = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;
const fmtDec = (n) => (n||0).toFixed(1);

// ── Pagination bar ───────────────────────────────────────────────
// Numbered pagination with ellipsis for large page counts.
// Works off whatever list is passed in (already filtered/searched) —
// caller controls the slicing.
function PaginationBar({ page, totalPages, onChange, totalItems, pageSize, T }) {
  if (totalPages <= 1) return null;

  const delta = 2;
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, totalItems);

  const btnBase = {
    minWidth:28, height:28, padding:"0 7px", borderRadius:6,
    border:`1px solid ${T.border}`, background:"none", color:T.muted,
    cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:600,
  };

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"12px 18px", borderTop:`1px solid ${T.border}`, flexWrap:"wrap", gap:8 }}>
      <div style={{ fontSize:11.5, color:T.muted }}>
        Showing <strong style={{ color:T.text }}>{start}</strong>–<strong style={{ color:T.text }}>{end}</strong> of <strong style={{ color:T.text }}>{totalItems}</strong>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:4 }}>
        <button disabled={page===1} onClick={() => onChange(page - 1)}
          style={{ ...btnBase, opacity: page===1 ? 0.4 : 1, cursor: page===1 ? "not-allowed" : "pointer" }}>
          ‹
        </button>
        {pages.map((p, i) => p === "…" ? (
          <span key={`gap-${i}`} style={{ color:T.dim, fontSize:11.5, padding:"0 3px" }}>…</span>
        ) : (
          <button key={p} onClick={() => onChange(p)}
            style={{ ...btnBase,
              background: p===page ? T.cyan : "none",
              borderColor: p===page ? T.cyan : T.border,
              color: p===page ? "#fff" : T.muted,
              fontWeight: p===page ? 800 : 600 }}>
            {p}
          </button>
        ))}
        <button disabled={page===totalPages} onClick={() => onChange(page + 1)}
          style={{ ...btnBase, opacity: page===totalPages ? 0.4 : 1, cursor: page===totalPages ? "not-allowed" : "pointer" }}>
          ›
        </button>
      </div>
    </div>
  );
}

export default function ProcessPayroll() {
  const { T } = useTheme();
  const STATUS_COLOR = {
    Draft:T.muted, Processing:T.cyan, Completed:T.amber, Paid:T.green
  };
  const [runs,       setRuns]       = useState([]);
  const [payslips,   setPayslips]   = useState([]);
  const [selRun,     setSelRun]     = useState(null);
  const [selMonth,   setSelMonth]   = useState(new Date().toISOString().slice(0,7));
  const [workDays,   setWorkDays]   = useState(22);
  const [processing, setProcessing] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [expanded,   setExpanded]   = useState(null);

  // ── Run History — search & pagination ─────────────────────────
  const [runSearch, setRunSearch] = useState("");
  const [runPage,   setRunPage]   = useState(1);
  const RUN_PAGE_SIZE = 8;

  // ── Individual Payslips — search & pagination ──────────────────
  const [payslipSearch, setPayslipSearch] = useState("");
  const [payslipPage,   setPayslipPage]   = useState(1);
  const PAYSLIP_PAGE_SIZE = 15;

  // ── Loan Deductions — summary for selMonth + upload modal ──────
  const [loanDeductions,  setLoanDeductions]  = useState([]);
  const [loanLoading,     setLoanLoading]     = useState(false);
  const [loanModalOpen,   setLoanModalOpen]   = useState(false);
  const [loanFile,        setLoanFile]        = useState(null);
  const [loanDragOver,    setLoanDragOver]    = useState(false);
  const [loanParsed,      setLoanParsed]      = useState(null);
  const [loanParseErrors, setLoanParseErrors] = useState([]);
  const [loanParseFail,   setLoanParseFail]   = useState("");
  const [loanUploading,   setLoanUploading]   = useState(false);
  const [loanResult,      setLoanResult]      = useState(null);
  const loanFileRef = useRef();

  // ── Payroll config (loaded from /payroll/config/working-days) ──
  const [payConfig, setPayConfig] = useState({
    working_days:  22,
    period_start:  "",
    period_end:    "",
    saturday_working: false,
    period_start_day: 1,
  });

  // ── Attendance Preview state ───────────────────────────
  const [preview,        setPreview]        = useState(null);
  const [showPreview,    setShowPreview]    = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Fix missed punches state ──────────────────────────
  const [fixingMissed, setFixingMissed] = useState(false);

  // ── Regenerate state ───────────────────────────────────
  const [regenRun,     setRegenRun]     = useState(null);
  const [regenDays,    setRegenDays]    = useState(22);
  const [regenLoading, setRegenLoading] = useState(false);
  const [loanFormatDetected, setLoanFormatDetected] = useState(""); // "long" | "wide"

  const fetchRuns = async () => {
    try {
      const r = await axios.get(APIConstant.payrollRuns, ax());
      setRuns(r.data || []);
    } catch(e) { console.error(e); }
  };

  const fetchPayslips = async (runId) => {
    try {
      setLoading(true);
      const r = await axios.get(`${APIConstant.payslips}run/${runId}`, ax());
      setPayslips(r.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ── Load payroll config for selected month ─────────────────────
  // Calls GET /payroll/config/working-days?month=YYYY-MM
  // Returns: { working_days, period_start, period_end, saturday_working, period_start_day }
  const loadConfig = async (month) => {
    try {
      const r = await axios.get(
        `${apiBaseUrl}/payroll/config/working-days?month=${month}`,
        ax()
      );
      const cfg = r.data || {};
      setPayConfig({
        working_days:     cfg.working_days     || 22,
        period_start:     cfg.period_start     || "",
        period_end:       cfg.period_end       || "",
        saturday_working: cfg.saturday_working || false,
        period_start_day: cfg.period_start_day || 1,
      });
      setWorkDays(cfg.working_days || 22);
      setRegenDays(cfg.working_days || 22);
    } catch {
      // fallback — don't break if config not set up yet
    }
  };

  useEffect(() => { fetchRuns(); }, []);

  // Reload config whenever the selected month changes
  useEffect(() => { loadConfig(selMonth); }, [selMonth]);

  // ── Loan Deductions — load summary for selMonth ─────────────────
  const fetchLoanDeductions = async (month) => {
    setLoanLoading(true);
    try {
      const r = await axios.get(`${apiBaseUrl}/loan-deductions/?month=${month}`, ax());
      setLoanDeductions(r.data || []);
    } catch (e) { setLoanDeductions([]); }
    finally { setLoanLoading(false); }
  };
  useEffect(() => { fetchLoanDeductions(selMonth); }, [selMonth]);

  const loanTotal = loanDeductions.reduce((sum, r) => sum + (r.amount || 0), 0);
  const loanUniqueEmployees = new Set(loanDeductions.map(r => r.employee_code)).size;

  const openLoanModal = () => {
  setLoanFile(null); setLoanParsed(null); setLoanParseErrors([]);
  setLoanParseFail(""); setLoanResult(null); setLoanFormatDetected(""); setLoanModalOpen(true);
};

  const cleanCell = (v) => (v === undefined || v === null) ? "" : String(v).trim();

const parseLoanFile = (f) => {
  setLoanParseFail("");
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb = XLSX.read(e.target.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!json.length) { setLoanParseFail("The sheet appears to be empty."); return; }

      const headers = Object.keys(json[0]);

      // Detect format: "long" (Employee Code / Loan Name / Deduction Amount
      // columns, one row per loan) vs "wide" (one row per employee, one
      // column per loan type — the loan name comes from the COLUMN HEADER
      // itself, e.g. "Share Loan", "MBT Loan", "Festival", "BMPL"...).
      const isLongFormat = headers.some(h => /loan\s*name/i.test(h)) &&
                            headers.some(h => /deduction\s*amount/i.test(h));

      const rowsOut = [];
      const errs = [];

      if (isLongFormat) {
        // ── Long format (unchanged from before) ──
        json.forEach((r, idx) => {
          const code     = cleanCell(r["Employee Code"]);
          const loanName = cleanCell(r["Loan Name"]);
          const amount   = parseFloat(r["Deduction Amount"]);
          const notes    = cleanCell(r["Notes"]) || null;
          if (!code || !loanName || isNaN(amount) || amount <= 0) {
            const missing = [];
            if (!code) missing.push("Employee Code");
            if (!loanName) missing.push("Loan Name");
            if (isNaN(amount) || amount <= 0) missing.push("Deduction Amount");
            errs.push({ row: idx + 2, reason: `missing/invalid: ${missing.join(", ")}` });
            return;
          }
          rowsOut.push({ employee_code: code, loan_name: loanName, amount, notes });
        });
        setLoanFormatDetected("long");
      } else {
        // ── Wide format — loan name comes from the COLUMN HEADER, not a
        // cell value. One row per employee; every non-empty loan-type
        // column on that row becomes its own {employee_code, loan_name,
        // amount} entry — exactly the same shape the backend already
        // expects, so nothing downstream needs to change.
        const idCol = headers.find(h => /emp.*id|employee.*id|emp.*code|employee.*code/i.test(h));
        if (!idCol) {
          setLoanParseFail('Could not find an "EMP ID" / "Employee Code" column in this file.');
          return;
        }
        // Columns that are clearly NOT loan types — serial number, name,
        // branch/location/department. Everything else is a candidate.
        const SKIP_PATTERNS = /^s\.?no\.?$|^sl\.?\s*no\.?$|^serial|employee\s*name|^name$|branch|location|department/i;
        const loanColsCandidate = headers.filter(h => h !== idCol && h && !SKIP_PATTERNS.test(h));

        // Auto-skip any loan column where every row is 0/blank across the
        // whole sheet — nothing to upload for a column nobody has an
        // amount in (e.g. this file's empty "TDS" and "Staff Fund" columns).
        const activeLoanCols = loanColsCandidate.filter(col =>
          json.some(r => { const v = parseFloat(r[col]); return !isNaN(v) && v > 0; })
        );

        if (activeLoanCols.length === 0) {
          setLoanParseFail("No loan columns with any non-zero amounts were found in this file.");
          return;
        }

        json.forEach((r, idx) => {
          const rawId = cleanCell(r[idCol]);
          if (!rawId) {
            errs.push({ row: idx + 2, reason: "missing Employee ID" });
            return;
          }
          const empCode = rawId;

          activeLoanCols.forEach(col => {
            const amount = parseFloat(r[col]);
            if (isNaN(amount) || amount <= 0) return; // nothing for this loan type on this row
            rowsOut.push({ employee_code: empCode, loan_name: col.trim(), amount, notes: null });
          });
        });
        setLoanFormatDetected("wide");
      }

      setLoanParsed(rowsOut);
      setLoanParseErrors(errs);
    } catch (err) {
      setLoanParseFail("Could not read this file. Please use the loan deductions template (.xlsx).");
    }
  };
  reader.readAsBinaryString(f);
};

  const handleLoanDrop = (e) => {
    e.preventDefault(); setLoanDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) { setLoanFile(f); parseLoanFile(f); }
  };
  const handleLoanFileInput = (e) => {
    const f = e.target.files[0];
    if (f) { setLoanFile(f); parseLoanFile(f); }
  };

  const downloadLoanTemplate = () => {
    const wb = XLSX.utils.book_new();
    const headers = ["Employee Code", "Loan Name", "Deduction Amount", "Notes"];
    // Sample shows BWDA0001 with TWO simultaneous loans — that's the
    // pattern for anyone with multiple loans: one row per loan, same
    // employee code repeated, different Loan Name each time.
    const sample = [
      ["BWDA0001", "Personal Loan",   5000, "Installment 4/12"],
      ["BWDA0001", "Salary Advance",  2000, "One-time recovery"],
      ["BWDA0002", "Bike Loan",       3200, "Installment 8/24"],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
    ws["!cols"] = [{ wch: 16 }, { wch: 20 }, { wch: 18 }, { wch: 32 }];
    XLSX.utils.book_append_sheet(wb, ws, "Loan Deductions");
    XLSX.writeFile(wb, "Loan_Deductions_Template.xlsx");
  };

  const submitLoanUpload = async () => {
    if (!loanParsed || loanParsed.length === 0) return;
    setLoanUploading(true);
    try {
      const res = await axios.post(`${apiBaseUrl}/loan-deductions/bulk-upsert`, {
        month: selMonth, rows: loanParsed,
      }, ax());
      setLoanResult(res.data);
      fetchLoanDeductions(selMonth);
    } catch (e) {
      setLoanResult({ error: e.response?.data?.detail || "Upload failed" });
    } finally {
      setLoanUploading(false);
    }
  };

  // ── Run History — filter + paginate ─────────────────────────────
  const filteredRuns = useMemo(() => runs.filter(r => {
    const q = runSearch.toLowerCase();
    return !q || r.month.toLowerCase().includes(q) || (r.status||"").toLowerCase().includes(q);
  }), [runs, runSearch]);

  useEffect(() => { setRunPage(1); }, [runSearch]);
  const runTotalPages = Math.max(1, Math.ceil(filteredRuns.length / RUN_PAGE_SIZE));
  useEffect(() => { if (runPage > runTotalPages) setRunPage(runTotalPages); }, [runTotalPages, runPage]);
  const paginatedRuns = useMemo(() => {
    const start = (runPage - 1) * RUN_PAGE_SIZE;
    return filteredRuns.slice(start, start + RUN_PAGE_SIZE);
  }, [filteredRuns, runPage]);

  // ── Individual Payslips — filter + paginate ─────────────────────
  const filteredPayslips = useMemo(() => payslips.filter(ps => {
    const q = payslipSearch.toLowerCase();
    return !q || `${ps.employee_name} ${ps.employee_code}`.toLowerCase().includes(q);
  }), [payslips, payslipSearch]);

  // Reset payslip page on new search, and whenever a different run is selected
  useEffect(() => { setPayslipPage(1); }, [payslipSearch, selRun]);
  const payslipTotalPages = Math.max(1, Math.ceil(filteredPayslips.length / PAYSLIP_PAGE_SIZE));
  useEffect(() => { if (payslipPage > payslipTotalPages) setPayslipPage(payslipTotalPages); }, [payslipTotalPages, payslipPage]);
  const paginatedPayslips = useMemo(() => {
    const start = (payslipPage - 1) * PAYSLIP_PAGE_SIZE;
    return filteredPayslips.slice(start, start + PAYSLIP_PAGE_SIZE);
  }, [filteredPayslips, payslipPage]);

  // ── Fix missed punches ────────────────────────────────
  const handleFixMissedPunches = async () => {
    setFixingMissed(true);
    try {
      const r = await axios.post(
        `${APIConstant.payrollRuns}/fix-missed-punches/?month=${selMonth}`,
        {}, ax()
      );
      const updated = await axios.get(
        `${APIConstant.payrollRuns}/attendance-preview/?month=${selMonth}&working_days=${workDays}`,
        ax()
      );
      setPreview(updated.data);
      alert(`✅ ${r.data.message}`);
    } catch(e) {
      alert("❌ " + (e.response?.data?.detail || "Error fixing missed punches"));
    } finally { setFixingMissed(false); }
  };

  // ── Step 1: Fetch attendance preview ──────────────────
  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await axios.get(
        `${APIConstant.payrollRuns}/attendance-preview/?month=${selMonth}&working_days=${workDays}`,
        ax()
      );
      setPreview(r.data);
      setShowPreview(true);
    } catch(e) {
      alert(e.response?.data?.detail || "Could not fetch attendance data.");
    } finally { setPreviewLoading(false); }
  };

  // ── Step 2: Confirmed by HR — actually generate ────────
  const handleConfirmAndGenerate = async () => {
    setShowPreview(false);
    setProcessing(true);
    try {
      const existingRuns = (await axios.get(APIConstant.payrollRuns, ax())).data;
      const existingRun  = existingRuns.find(r => r.month === selMonth);
      let runId;
      if (existingRun) {
        runId = existingRun.id;
      } else {
        const rRes = await axios.post(`${APIConstant.payrollRuns}?month=${selMonth}`, {}, ax());
        runId = rRes.data.id;
      }
      await axios.post(
        `${APIConstant.payrollRuns}/${runId}/process?working_days=${workDays}`,
        {}, ax()
      );
      await fetchRuns();
      const updatedRuns = (await axios.get(APIConstant.payrollRuns, ax())).data;
      const run = updatedRuns.find(r => r.month === selMonth);
      if (run) { setSelRun(run); fetchPayslips(run.id); }
    } catch(e) { alert(e.response?.data?.detail || "Error generating payroll"); }
    finally { setProcessing(false); }
  };

  // ── Regenerate existing run ────────────────────────────
  const handleRegenerate = async () => {
    if (!regenRun) return;
    if (regenRun.status === "Paid") {
      alert("Cannot regenerate a paid payroll run.");
      setRegenRun(null); return;
    }
    setRegenLoading(true);
    try {
      await axios.post(
        `${APIConstant.payrollRuns}/${regenRun.id}/process?working_days=${regenDays}`,
        {}, ax()
      );
      await fetchRuns();
      const updatedRuns = (await axios.get(APIConstant.payrollRuns, ax())).data;
      const updated = updatedRuns.find(r => r.id === regenRun.id);
      if (updated) { setSelRun(updated); fetchPayslips(updated.id); }
      setRegenRun(null);
    } catch(e) { alert(e.response?.data?.detail || "Error regenerating payslips"); }
    finally { setRegenLoading(false); }
  };

  // ── Mark Paid — background email progress tracking ──────────────
  // The backend returns instantly now (mark_paid is async in the sense
  // that PDF gen + email sending happens after the response, via
  // BackgroundTasks) so this polls /email-status every 3s to show
  // real progress instead of the UI just freezing on 500 employees.
  const [emailProgress, setEmailProgress] = useState(null); // {email_status, emails_total, emails_sent, emails_failed}
  const pollRef = useRef(null);

  const pollEmailStatus = (runId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${APIConstant.payrollRuns}/${runId}/email-status`, ax());
        setEmailProgress(r.data);
        if (r.data.email_status === "Completed" || r.data.email_status === "Completed with Errors") {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) { /* transient — keep polling */ }
    }, 3000);
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleMarkPaid = async () => {
    if (!selRun) return;
    try {
      const res = await axios.put(`${APIConstant.payrollRuns}/${selRun.id}/mark-paid`, {}, ax());
      fetchRuns();
      setSelRun(p => ({...p, status:"Paid"}));
      setPayslips(p => p.map(ps => ({...ps, status:"Paid"})));
      setEmailProgress({
        email_status: res.data.email_status || "Not Started",
        emails_total: 0, emails_sent: 0, emails_failed: 0,
      });
      pollEmailStatus(selRun.id);
    } catch(e) { console.error(e); }
  };

  const selectRun = (run) => {
    setSelRun(run); fetchPayslips(run.id);
    setEmailProgress(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    // If this run was already marked Paid in a previous session, fetch
    // its email status once so the badge reflects reality on reselect
    // (no polling needed unless it's still actively Sending).
    if (run.status === "Paid") {
      axios.get(`${APIConstant.payrollRuns}/${run.id}/email-status`, ax())
        .then(r => {
          setEmailProgress(r.data);
          if (r.data.email_status === "Sending") pollEmailStatus(run.id);
        })
        .catch(() => {});
    }
  };

  const getComponents = (ps) => {
    try { return JSON.parse(typeof ps.components === "string" ? ps.components : JSON.stringify(ps.components||{})); }
    catch { return {}; }
  };

  const hasWarnings     = preview?.warnings?.length > 0;
  const hasNoAttendance = preview?.no_attendance_count > 0;
  const hasNoSalary     = preview?.no_salary_count > 0;

  // ── Period label ──────────────────────────────────────
  const periodLabel = payConfig.period_start && payConfig.period_end
    ? `${payConfig.period_start} → ${payConfig.period_end}`
    : null;

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>COMPENSATION</div>
          <h2>Process Payroll</h2>
          <p>Review attendance, confirm LOP and generate monthly payslips</p>
        </div>
      </div>

      <div className={`${styles.contentRow} ${styles.col13}`}>

        {/* ── Left panel ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* New run card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}><h3>New Payroll Run</h3></div>
            <div className={styles.cardBody} style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:5,
                  textTransform:"uppercase", letterSpacing:"0.06em" }}>Month</label>
                <input type="month" value={selMonth} onChange={e => setSelMonth(e.target.value)}
                  style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.cyan}26`,
                    borderRadius:8, color:T.text, fontSize:13, padding:"8px 12px",
                    outline:"none", fontFamily:"Outfit,sans-serif" }}/>
              </div>

              {/* ── Period info from config ── */}
              {periodLabel && (
                <div style={{ background:`${T.cyan}0D`, border:`1px solid ${T.cyan}1F`,
                  borderRadius:8, padding:"9px 12px", display:"flex", alignItems:"center", gap:8 }}>
                  <Calendar size={13} style={{ color:T.cyan, flexShrink:0 }}/>
                  <div>
                    <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase",
                      letterSpacing:"0.06em", marginBottom:2 }}>Payroll Period</div>
                    <div style={{ fontSize:12.5, color:T.cyan, fontFamily:"JetBrains Mono,monospace",
                      fontWeight:600 }}>{periodLabel}</div>
                  </div>
                  <div style={{ marginLeft:"auto", textAlign:"right" }}>
                    <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase",
                      letterSpacing:"0.06em", marginBottom:2 }}>Working Days</div>
                    <div style={{ fontSize:16, fontWeight:800,
                      fontFamily:"JetBrains Mono,monospace", color:T.green }}>
                      {payConfig.working_days}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:5,
                  textTransform:"uppercase", letterSpacing:"0.06em" }}>
                  Working Days
                  <span style={{ fontSize:10.5, color:T.dim, marginLeft:6,
                    textTransform:"none", letterSpacing:0 }}>
                    (auto from config, override if needed)
                  </span>
                </label>
                <input type="number" min={1} max={31} value={workDays}
                  onChange={e => setWorkDays(parseInt(e.target.value)||22)}
                  style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.cyan}26`,
                    borderRadius:8, color:T.text, fontSize:13, padding:"8px 12px",
                    outline:"none", fontFamily:"Outfit,sans-serif" }}/>
              </div>

              <div style={{ background:`${T.cyan}0D`, border:`1px solid ${T.cyan}1F`,
                borderRadius:8, padding:"10px 14px", fontSize:12, color:T.muted, lineHeight:1.7 }}>
                ℹ️ <strong style={{ color:T.text }}>Preview & Review</strong> shows attendance summary before generating.
                LOP is auto-calculated from approved LOP-marked leaves only.
                {payConfig.saturday_working &&
                  <span style={{ color:T.amber }}> Saturday included as working day.</span>}
              </div>

              <button className={styles.btnPrimary}
                onClick={handlePreview}
                disabled={previewLoading || processing}
                style={{ justifyContent:"center" }}>
                {previewLoading
                  ? "Loading attendance…"
                  : processing
                    ? "Generating payslips…"
                    : <><Eye size={13}/> Preview & Generate</>}
              </button>
            </div>
          </div>

          {/* Run History */}
          <div className={styles.card}>
            <div className={styles.cardHeader}><h3>Run History</h3></div>
            <div style={{ padding:"10px 18px", borderBottom:`1px solid ${T.cyan}0F` }}>
              <div style={{ position:"relative" }}>
                <Search size={12} style={{ position:"absolute", left:9, top:"50%",
                  transform:"translateY(-50%)", color:T.dim }}/>
                <input value={runSearch} onChange={e => setRunSearch(e.target.value)}
                  placeholder="Search by month or status…"
                  style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
                    borderRadius:7, color:T.text, fontSize:12, padding:"6px 10px 6px 26px",
                    outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
              </div>
            </div>
            <div style={{ maxHeight:400, overflowY:"auto" }}>
              {paginatedRuns.map(r => (
                <div key={r.id} style={{ borderBottom:`1px solid ${T.cyan}0F` }}>
                  <div onClick={() => selectRun(r)}
                    style={{ padding:"12px 18px", cursor:"pointer",
                      background:selRun?.id===r.id?`${T.cyan}0F`:"transparent",
                      transition:"background 0.15s" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontSize:13, fontWeight:600,
                        fontFamily:"JetBrains Mono,monospace", color:T.text }}>{r.month}</span>
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                        background:`${STATUS_COLOR[r.status]}20`,
                        color:STATUS_COLOR[r.status] }}>{r.status}</span>
                    </div>
                    <div style={{ fontSize:11.5, color:T.muted, marginTop:4 }}>
                      {r.total_employees} employees · Net {fmt(r.total_net)}
                    </div>
                  </div>
                  {r.status !== "Paid" && (
                    <div style={{ padding:"0 18px 10px" }}>
                      <button onClick={e => { e.stopPropagation(); setRegenRun(r); setRegenDays(workDays); }}
                        style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px",
                          borderRadius:6, fontSize:11.5, fontWeight:600,
                          border:`1px solid ${T.amber}40`, background:`${T.amber}0F`,
                          color:T.amber, cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
                        <RefreshCw size={11}/> Regenerate
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {filteredRuns.length === 0 && (
                <div style={{ textAlign:"center", padding:24, color:T.muted, fontSize:13 }}>No runs found</div>
              )}
            </div>
            <PaginationBar
              page={runPage}
              totalPages={runTotalPages}
              onChange={setRunPage}
              totalItems={filteredRuns.length}
              pageSize={RUN_PAGE_SIZE}
              T={T}
            />
          </div>
        </div>

        {/* ── Right panel ── */}
        <div>
          {!selRun ? (
            <div className={styles.card} style={{ display:"flex", flexDirection:"column",
              alignItems:"center", justifyContent:"center", minHeight:400 }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
              <div style={{ fontSize:14, color:T.muted, textAlign:"center" }}>
                Select a payroll run from the left<br/>or generate a new one
              </div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div>
                    <h3>Payroll Run — {selRun.month}</h3>
                    <p style={{ margin:"4px 0 0", fontSize:12, color:T.muted }}>
                      {selRun.total_employees} employees processed
                    </p>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <span style={{ fontSize:12, padding:"4px 12px", borderRadius:20,
                      background:`${STATUS_COLOR[selRun.status]}20`,
                      color:STATUS_COLOR[selRun.status], fontWeight:600 }}>{selRun.status}</span>
                    {selRun.status === "Paid" && emailProgress && emailProgress.email_status !== "Not Started" && (
                      <span style={{ fontSize:11.5, padding:"4px 10px", borderRadius:20,
                        display:"flex", alignItems:"center", gap:5,
                        background: emailProgress.email_status === "Sending" ? `${T.cyan}18`
                          : emailProgress.email_status === "Completed" ? `${T.green}18` : `${T.amber}18`,
                        color: emailProgress.email_status === "Sending" ? T.cyan
                          : emailProgress.email_status === "Completed" ? T.green : T.amber,
                        fontWeight:600 }}>
                        {emailProgress.email_status === "Sending" && (
                          <span style={{ width:8, height:8, borderRadius:"50%",
                            border:`2px solid currentColor`, borderTopColor:"transparent",
                            display:"inline-block", animation:"spin 0.8s linear infinite" }}/>
                        )}
                        {emailProgress.email_status === "Sending"
                          ? `Sending payslips: ${emailProgress.emails_sent}/${emailProgress.emails_total}`
                          : emailProgress.email_status === "Completed"
                            ? `✓ ${emailProgress.emails_sent} payslip email${emailProgress.emails_sent!==1?"s":""} sent`
                            : `${emailProgress.emails_sent} sent, ${emailProgress.emails_failed} failed`}
                      </span>
                    )}
                    {selRun.status === "Completed" && (
                      <button className={styles.btnPrimary} onClick={handleMarkPaid}>
                        <CheckCircle size={13}/> Mark All Paid
                      </button>
                    )}
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                    {[
                      { label:"Total Gross",     val:fmt(selRun.total_gross),      color:T.amber },
                      { label:"Total Deductions", val:fmt(selRun.total_deductions), color:T.red   },
                      { label:"Total Net Pay",    val:fmt(selRun.total_net),        color:T.green },
                    ].map(({ label, val, color }) => (
                      <div key={label} style={{ background:T.toggle_bg,
                        border:`1px solid ${T.border}`, borderRadius:10, padding:14 }}>
                        <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase",
                          letterSpacing:"0.07em", marginBottom:6 }}>{label}</div>
                        <div style={{ fontSize:20, fontWeight:800,
                          fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3>Individual Payslips</h3>
                  <span className={styles.cardMeta}>{filteredPayslips.length} records</span>
                </div>
                <div style={{ padding:"10px 22px", borderBottom:`1px solid ${T.cyan}0F` }}>
                  <div style={{ position:"relative", maxWidth:280 }}>
                    <Search size={12} style={{ position:"absolute", left:9, top:"50%",
                      transform:"translateY(-50%)", color:T.dim }}/>
                    <input value={payslipSearch} onChange={e => setPayslipSearch(e.target.value)}
                      placeholder="Search employee…"
                      style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
                        borderRadius:7, color:T.text, fontSize:12, padding:"6px 10px 6px 26px",
                        outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
                  </div>
                </div>
                {loading ? <div className={styles.loader}><div className={styles.spinner}/></div> : (
                  <div>
                    {paginatedPayslips.map(ps => {
                      const comps = getComponents(ps);
                      const isExp = expanded === ps.id;
                      return (
                        <div key={ps.id} style={{ borderBottom:`1px solid ${T.cyan}0F` }}>
                          <div style={{ padding:"13px 22px", display:"flex", alignItems:"center",
                            gap:12, cursor:"pointer" }}
                            onClick={() => setExpanded(isExp ? null : ps.id)}>
                            <div style={{ width:36, height:36, borderRadius:9,
                              background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:12, fontWeight:700, color:"#fff", flexShrink:0 }}>
                              {ps.employee_name?.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                            </div>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:13, fontWeight:500 }}>{ps.employee_name}</div>
                              <div style={{ fontSize:11.5, color:T.muted }}>
                                {ps.employee_code} · {ps.department}
                              </div>
                            </div>
                            <div style={{ textAlign:"right" }}>
                              <div style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace",
                                color:T.green, fontWeight:700 }}>{fmt(ps.net_salary)}</div>
                              <div style={{ fontSize:11, color:T.muted }}>Gross: {fmt(ps.gross_salary)}</div>
                            </div>
                            {isExp
                              ? <ChevronUp size={14} style={{ color:T.muted }}/>
                              : <ChevronDown size={14} style={{ color:T.muted }}/>}
                          </div>
                          {isExp && (
                            <div style={{ padding:"0 22px 16px", background:T.card2 }}>
                              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                                <div>
                                  <div style={{ fontSize:11, color:T.dim, textTransform:"uppercase",
                                    letterSpacing:"0.1em", marginBottom:10 }}>Earnings</div>
                                  {Object.entries(comps.earnings || {}).map(([k,v]) => (
                                    <div key={k} style={{ display:"flex", justifyContent:"space-between",
                                      marginBottom:7, fontSize:12.5 }}>
                                      <span style={{ color:T.muted }}>{k}</span>
                                      <span style={{ color:T.green,
                                        fontFamily:"JetBrains Mono,monospace" }}>{fmt(v)}</span>
                                    </div>
                                  ))}
                                  {ps.lop_amount > 0 && (
                                    <div style={{ display:"flex", justifyContent:"space-between",
                                      marginBottom:7, fontSize:12.5 }}>
                                      <span style={{ color:T.red }}>LOP ({ps.lop_days} days)</span>
                                      <span style={{ color:T.red,
                                        fontFamily:"JetBrains Mono,monospace" }}>-{fmt(ps.lop_amount)}</span>
                                    </div>
                                  )}
                                  <div style={{ display:"flex", justifyContent:"space-between",
                                    padding:"8px 0", borderTop:`1px solid ${T.border}`,
                                    fontSize:13, fontWeight:700 }}>
                                    <span>Gross</span>
                                    <span style={{ color:T.amber,
                                      fontFamily:"JetBrains Mono,monospace" }}>{fmt(ps.gross_salary)}</span>
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize:11, color:T.dim, textTransform:"uppercase",
                                    letterSpacing:"0.1em", marginBottom:10 }}>Deductions</div>
                                  {(() => {
                                    // PF/ESI/PT/TDS come from their own dedicated Payslip fields
                                    // (shown first, in a fixed order). Everything else in
                                    // comps.deductions — which is where each individually-named
                                    // loan lives (e.g. "Personal Loan", "Salary Advance") — is
                                    // rendered dynamically below, one row per loan, instead of
                                    // being collapsed into a single "Loan Deduction" total.
                                    const KNOWN = ["PF Employee","PF","PF Employer","Employer PF",
                                      "ESI Employee","ESI","ESI Employer","Professional Tax","PT"];
                                    const otherDeductions = Object.entries(comps.deductions || {})
                                      .filter(([name]) => !KNOWN.includes(name));
                                    return [
                                      { label:"PF (Employee)",    val:ps.pf_employee      },
                                      { label:"ESI (Employee)",   val:ps.esi_employee     },
                                      { label:"Professional Tax", val:ps.professional_tax },
                                      ...otherDeductions.map(([name, val]) => ({ label:name, val })),
                                      { label:"TDS",              val:ps.tds              },
                                    ];
                                  })().filter(d => d.val > 0).map(({ label, val }) => (
                                    <div key={label} style={{ display:"flex", justifyContent:"space-between",
                                      marginBottom:7, fontSize:12.5 }}>
                                      <span style={{ color:T.muted }}>{label}</span>
                                      <span style={{ color:T.red,
                                        fontFamily:"JetBrains Mono,monospace" }}>-{fmt(val)}</span>
                                    </div>
                                  ))}
                                  <div style={{ display:"flex", justifyContent:"space-between",
                                    padding:"8px 0", borderTop:`1px solid ${T.border}`,
                                    fontSize:13, fontWeight:700 }}>
                                    <span>Net Pay</span>
                                    <span style={{ color:T.green,
                                      fontFamily:"JetBrains Mono,monospace" }}>{fmt(ps.net_salary)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {filteredPayslips.length === 0 && (
                      <div style={{ textAlign:"center", padding:32, color:T.muted, fontSize:13 }}>
                        No payslips found
                      </div>
                    )}
                  </div>
                )}
                {!loading && (
                  <PaginationBar
                    page={payslipPage}
                    totalPages={payslipTotalPages}
                    onChange={setPayslipPage}
                    totalItems={filteredPayslips.length}
                    pageSize={PAYSLIP_PAGE_SIZE}
                    T={T}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          ATTENDANCE PREVIEW MODAL — unchanged
      ══════════════════════════════════════════════════ */}
      {showPreview && preview && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:9998,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"Outfit,sans-serif", padding:20 }}
          onClick={() => setShowPreview(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`,
            borderRadius:16, width:"100%", maxWidth:820,
            maxHeight:"88vh", overflow:"hidden", display:"flex", flexDirection:"column" }}
            onClick={e => e.stopPropagation()}>

            <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${T.border}`,
              background:`${T.cyan}0A`, flexShrink:0 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <h3 style={{ margin:"0 0 4px", fontSize:17, fontWeight:700, color:T.text }}>
                    Attendance Review — {preview.month}
                  </h3>
                  <div style={{ fontSize:12.5, color:T.muted }}>
                    {preview.period_start && preview.period_end
                      ? <>Period: <strong style={{ color:T.cyan }}>{preview.period_start} → {preview.period_end}</strong> · </>
                      : null}
                    Working days: <strong style={{ color:T.text }}>{preview.working_days}</strong>
                  </div>
                </div>
                <button onClick={() => setShowPreview(false)}
                  style={{ background:"none", border:"none", cursor:"pointer",
                    color:T.muted, fontSize:20, lineHeight:1 }}>✕</button>
              </div>

              <div style={{ display:"flex", gap:10, marginTop:14, flexWrap:"wrap" }}>
                <div style={{ padding:"6px 14px", borderRadius:20, background:`${T.cyan}1A`,
                  border:`1px solid ${T.cyan}33`, fontSize:12, color:T.cyan,
                  display:"flex", alignItems:"center", gap:6 }}>
                  <Users size={12}/> {preview.total_employees} Employees
                </div>
                <div style={{ padding:"6px 14px", borderRadius:20, background:`${T.green}1A`,
                  border:`1px solid ${T.green}33`, fontSize:12, color:T.green }}>
                  ✓ {preview.ready_count} Ready to process
                </div>
                {hasNoAttendance && (
                  <div style={{ padding:"6px 14px", borderRadius:20, background:`${T.amber}1A`,
                    border:`1px solid ${T.amber}40`, fontSize:12, color:T.amber,
                    display:"flex", alignItems:"center", gap:6 }}>
                    <AlertTriangle size={12}/> {preview.no_attendance_count} No attendance data
                  </div>
                )}
                {preview?.absent_no_lop_count > 0 && (
                  <div style={{ padding:"6px 14px", borderRadius:20, background:`${T.purple}1A`,
                    border:`1px solid ${T.purple}40`, fontSize:12, color:T.purple,
                    display:"flex", alignItems:"center", gap:6 }}>
                    <AlertTriangle size={12}/> {preview.absent_no_lop_count} Absent — LOP not marked
                  </div>
                )}
                {preview?.total_missed_punch > 0 && (
                  <div style={{ padding:"6px 14px", borderRadius:20, background:`${T.red}1A`,
                    border:`1px solid ${T.red}40`, fontSize:12, color:T.red,
                    display:"flex", alignItems:"center", gap:6 }}>
                    <AlertTriangle size={12}/> {preview.total_missed_punch} Missed Punch Days
                  </div>
                )}
                {hasNoSalary && (
                  <div style={{ padding:"6px 14px", borderRadius:20, background:`${T.red}1A`,
                    border:`1px solid ${T.red}40`, fontSize:12, color:T.red,
                    display:"flex", alignItems:"center", gap:6 }}>
                    <AlertTriangle size={12}/> {preview.no_salary_count} No salary assigned
                  </div>
                )}
              </div>
            </div>

            {preview?.total_missed_punch > 0 && (
              <div style={{ padding:"12px 24px", background:`${T.red}0D`,
                borderBottom:`1px solid ${T.red}1A`,
                display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
                <div style={{ fontSize:12.5, color:T.red }}>
                  <strong>{preview.total_missed_punch}</strong> working day(s) across all employees
                  have no attendance record and no approved leave.
                </div>
                <button onClick={handleFixMissedPunches} disabled={fixingMissed}
                  style={{ flexShrink:0, marginLeft:16, padding:"7px 16px", borderRadius:8, border:"none",
                    background:fixingMissed?T.toggle_bg:`linear-gradient(135deg,${T.red},${T.amber})`,
                    color:fixingMissed?T.muted:"#fff",
                    cursor:fixingMissed?"not-allowed":"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:12.5, fontWeight:700, whiteSpace:"nowrap" }}>
                  {fixingMissed ? "Marking…" : "✓ Mark All as Present"}
                </button>
              </div>
            )}

            {hasWarnings && (
              <div style={{ padding:"10px 24px", background:`${T.amber}0D`,
                borderBottom:`1px solid ${T.amber}1F`, flexShrink:0,
                maxHeight:120, display:"flex", flexDirection:"column" }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.amber, marginBottom:5,
                  textTransform:"uppercase", letterSpacing:"0.07em", flexShrink:0 }}>
                  ⚠️ Warnings ({preview.warnings.length})
                </div>
                <div style={{ overflowY:"auto", display:"flex", flexDirection:"column", gap:3 }}>
                  {preview.warnings.map((w, i) => (
                    <div key={i} style={{ fontSize:12, color:T.amber,
                      display:"flex", alignItems:"flex-start", gap:6 }}>
                      <span style={{ color:`${T.amber}80`, flexShrink:0 }}>›</span>
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Loan Deductions — before the attendance records table ── */}
            <div style={{ padding:"14px 24px", background:`${T.cyan}06`,
              borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                flexWrap:"wrap", gap:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ fontSize:12.5, fontWeight:700, color:T.text, display:"flex",
                    alignItems:"center", gap:6 }}>
                    💰 Loan Deductions
                  </div>
                  {loanLoading ? (
                    <span style={{ fontSize:12, color:T.muted }}>Loading…</span>
                  ) : (
                    <span style={{ fontSize:12, color:T.muted }}>
                      <strong style={{ color:T.cyan }}>{loanDeductions.length}</strong> loan{loanDeductions.length!==1?"s":""} ·{" "}
                      <strong style={{ color:T.cyan }}>{loanUniqueEmployees}</strong> employee{loanUniqueEmployees!==1?"s":""} ·{" "}
                      <strong style={{ color:T.red }}>{fmt(loanTotal)}</strong> total this month
                    </span>
                  )}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={downloadLoanTemplate}
                    style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px",
                      borderRadius:7, border:`1px solid ${T.border}`, background:"none",
                      color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:600 }}>
                    <Download size={12}/> Template
                  </button>
                  <button onClick={openLoanModal}
                    style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px",
                      borderRadius:7, border:"none", background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                      color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12, fontWeight:700 }}>
                    <Upload size={12}/> Upload for {preview.month}
                  </button>
                </div>
              </div>
              {loanDeductions.length === 0 && !loanLoading && (
                <div style={{ fontSize:11.5, color:T.muted, marginTop:6 }}>
                  No loan deductions uploaded for this month yet — upload here if any employee has one, or skip.
                </div>
              )}
            </div>

            <div style={{ overflowY:"auto", flex:1 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead style={{ position:"sticky", top:0, background:T.card, zIndex:1 }}>
                  <tr style={{ borderBottom:`1px solid ${T.cyan}1A` }}>
                    {["Employee","Code","Present","Absent (Info)","Missed Punch","LOP (Manual)","LOP Deduction","Status"].map(h => (
                      <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:10.5,
                        color:T.muted, textTransform:"uppercase",
                        letterSpacing:"0.07em", fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.employees.map((emp, i) => (
                    <tr key={emp.employee_id}
                      style={{ borderBottom:`1px solid ${T.cyan}0A`,
                        background:emp.no_attendance?`${T.amber}0A`:!emp.has_salary?`${T.red}0A`:i%2===0?"transparent":T.card2 }}>
                      <td style={{ padding:"11px 14px", fontWeight:500, color:T.text }}>{emp.employee_name}</td>
                      <td style={{ padding:"11px 14px", fontFamily:"JetBrains Mono,monospace", fontSize:11.5, color:T.muted }}>{emp.employee_code}</td>
                      <td style={{ padding:"11px 14px", fontFamily:"JetBrains Mono,monospace", color:T.green, fontWeight:700 }}>{emp.present_days}</td>
                      <td style={{ padding:"11px 14px", fontFamily:"JetBrains Mono,monospace",
                        color:emp.absent_days>0?T.purple:T.muted }}>
                        {emp.absent_days > 0
                          ? <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                              {emp.absent_days}<span style={{ fontSize:10, color:T.purple }}>ℹ</span>
                            </span>
                          : "0"}
                      </td>
                      <td style={{ padding:"11px 14px", fontFamily:"JetBrains Mono,monospace",
                        color:emp.missed_punch_count>0?T.red:T.muted,
                        fontWeight:emp.missed_punch_count>0?700:400 }}>
                        {emp.missed_punch_count > 0
                          ? <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                              {emp.missed_punch_count}<span style={{ fontSize:10, color:T.red }}>!</span>
                            </span>
                          : <span style={{ color:T.green }}>0</span>}
                      </td>
                      <td style={{ padding:"11px 14px", fontFamily:"JetBrains Mono,monospace",
                        color:emp.lop_days>0?T.red:T.muted, fontWeight:emp.lop_days>0?700:400 }}>
                        {emp.lop_days > 0 ? fmtDec(emp.lop_days) : "—"}
                      </td>
                      <td style={{ padding:"11px 14px", fontFamily:"JetBrains Mono,monospace",
                        color:emp.lop_amount>0?T.red:T.muted }}>
                        {emp.lop_amount > 0 ? `-${fmt(emp.lop_amount)}` : "—"}
                      </td>
                      <td style={{ padding:"11px 14px" }}>
                        {!emp.has_salary ? (
                          <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                            background:`${T.red}1F`, color:T.red }}>No Salary ⚠️</span>
                        ) : emp.missed_punch_count > 0 ? (
                          <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                            background:`${T.red}1F`, color:T.red }}>
                            Missed Punch ({emp.missed_punch_count}d) ⚠️
                          </span>
                        ) : emp.no_attendance ? (
                          <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                            background:`${T.amber}1F`, color:T.amber }}>No Attendance ⚠️</span>
                        ) : emp.absent_days > 0 && emp.lop_days === 0 ? (
                          <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                            background:`${T.purple}1F`, color:T.purple }}>Absent — Review LOP</span>
                        ) : (
                          <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                            background:`${T.green}1A`, color:T.green }}>Ready ✓</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ padding:"16px 24px", borderTop:`1px solid ${T.border}`,
              background:T.card, display:"flex", justifyContent:"space-between",
              alignItems:"center", flexShrink:0 }}>
              <div style={{ fontSize:12.5, color:T.muted }}>
                {preview.ready_count === 0
                  ? "⚠️ No employees are ready — assign salary structures first."
                  : `${preview.ready_count} of ${preview.total_employees} employees will be processed.`}
                {hasNoAttendance && preview.ready_count > 0 &&
                  <span style={{ color:T.amber }}> Employees with no attendance get full salary.</span>}
                {preview?.absent_no_lop_count > 0 &&
                  <span style={{ color:T.purple }}> {preview.absent_no_lop_count} employee(s) have absent days not marked as LOP.</span>}
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setShowPreview(false)}
                  style={{ padding:"9px 18px", borderRadius:8,
                    border:`1px solid ${T.toggle_bg}`, background:"none",
                    color:T.muted, cursor:"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:13 }}>Cancel</button>
                <button onClick={handleConfirmAndGenerate} disabled={preview.ready_count === 0}
                  style={{ padding:"9px 22px", borderRadius:8, border:"none",
                    background:preview.ready_count===0?T.toggle_bg:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                    color:preview.ready_count===0?T.muted:"#fff",
                    cursor:preview.ready_count===0?"not-allowed":"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700,
                    display:"flex", alignItems:"center", gap:7 }}>
                  <Play size={13}/> Confirm & Generate Payslips
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Regenerate Modal ── */}
      {regenRun && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9998,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"Outfit,sans-serif" }}
          onClick={() => setRegenRun(null)}>
          <div style={{ background:T.card, border:`1px solid ${T.amber}4D`,
            borderRadius:14, width:420, padding:28 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <RefreshCw size={20} style={{ color:T.amber }}/>
              <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:T.amber }}>Regenerate Payslips</h3>
            </div>
            <p style={{ fontSize:13, color:T.muted, marginBottom:16, lineHeight:1.7 }}>
              Regenerating <strong style={{ color:T.text }}>{regenRun.month}</strong> will recalculate
              all payslips using the latest attendance, leave and salary data.
            </p>
            <div style={{ background:`${T.amber}0D`, border:`1px solid ${T.amber}26`,
              borderRadius:8, padding:"10px 14px", fontSize:12.5, color:T.amber, marginBottom:20 }}>
              ℹ️ Working days pre-filled from your payroll config. Override if needed.
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:6,
                textTransform:"uppercase", letterSpacing:"0.06em" }}>Working Days</label>
              <input type="number" min={1} max={31} value={regenDays}
                onChange={e => setRegenDays(parseInt(e.target.value)||22)}
                style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.cyan}26`,
                  borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px",
                  outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setRegenRun(null)}
                style={{ padding:"9px 18px", borderRadius:8,
                  border:`1px solid ${T.toggle_bg}`, background:"none",
                  color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13 }}>Cancel</button>
              <button onClick={handleRegenerate} disabled={regenLoading}
                style={{ padding:"9px 20px", borderRadius:8, border:"none",
                  background:`linear-gradient(135deg,${T.amber},#FF9500)`,
                  color:"#fff", cursor:regenLoading?"not-allowed":"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700,
                  opacity:regenLoading?0.7:1, display:"flex", alignItems:"center", gap:6 }}>
                <RefreshCw size={13}/>{regenLoading ? "Regenerating…" : "Yes, Regenerate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Loan Deductions Upload Modal ── */}
      {loanModalOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9999,
          display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
          onClick={e => { if (e.target === e.currentTarget && !loanUploading) setLoanModalOpen(false); }}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14,
            width:"100%", maxWidth:560, maxHeight:"88vh", overflow:"hidden",
            display:"flex", flexDirection:"column", fontFamily:"Outfit,sans-serif" }}>

            <div style={{ padding:"16px 22px", borderBottom:`1px solid ${T.border}`,
              background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:T.text }}>Upload Loan Deductions</h3>
                <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>For {selMonth}</div>
              </div>
              <button onClick={() => setLoanModalOpen(false)} disabled={loanUploading}
                style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={16}/></button>
            </div>

            <div style={{ padding:22, overflowY:"auto" }}>
              {!loanParsed && (
                <>
                  <div
                    onDragOver={e => { e.preventDefault(); setLoanDragOver(true); }}
                    onDragLeave={() => setLoanDragOver(false)}
                    onDrop={handleLoanDrop}
                    onClick={() => loanFileRef.current.click()}
                    style={{
                      border:`2px dashed ${loanDragOver ? T.cyan : T.border}`,
                      borderRadius:12, padding:"36px 20px", textAlign:"center", cursor:"pointer",
                      background: loanDragOver ? `${T.cyan}0D` : "transparent",
                    }}>
                    <FileSpreadsheet size={30} style={{ color:T.cyan, marginBottom:10 }}/>
                    <div style={{ fontSize:14, fontWeight:700, marginBottom:4 }}>Drop the Excel file here</div>
                    <div style={{ fontSize:12, color:T.muted }}>or click to browse · .xlsx</div>
                    {loanFile && <div style={{ marginTop:10, fontSize:12.5, color:T.green, fontWeight:600 }}>✓ {loanFile.name}</div>}
                  </div>
                  <input ref={loanFileRef} type="file" accept=".xlsx,.xls" onChange={handleLoanFileInput} style={{ display:"none" }}/>
                  {loanParseFail && (
                    <div style={{ marginTop:12, padding:"9px 14px", borderRadius:8, background:`${T.red}1A`,
                      border:`1px solid ${T.red}40`, color:T.red, fontSize:12.5 }}>⚠️ {loanParseFail}</div>
                  )}
                </>
              )}

              {loanParsed && !loanResult && (
                <>
                  <div style={{ display:"flex", gap:10, marginBottom:16 }}>
                    <div style={{ flex:1, padding:12, borderRadius:8, background:`${T.green}0F`, border:`1px solid ${T.green}26`, textAlign:"center" }}>
                      <div style={{ fontSize:22, fontWeight:900, color:T.green }}>{loanParsed.length}</div>
                      <div style={{ fontSize:11, color:T.muted }}>Valid Rows</div>
                    </div>
                    {loanParseErrors.length > 0 && (
                      <div style={{ flex:1, padding:12, borderRadius:8, background:`${T.red}0F`, border:`1px solid ${T.red}26`, textAlign:"center" }}>
                        <div style={{ fontSize:22, fontWeight:900, color:T.red }}>{loanParseErrors.length}</div>
                        <div style={{ fontSize:11, color:T.muted }}>Skipped</div>
                      </div>
                    )}
                  </div>
                  {loanParseErrors.length > 0 && (
                    <div style={{ marginBottom:16, padding:"10px 14px", borderRadius:8,
                      background:`${T.red}0A`, border:`1px solid ${T.red}22`, maxHeight:100, overflowY:"auto" }}>
                      {loanParseErrors.slice(0, 6).map((e, i) => (
                        <div key={i} style={{ fontSize:11.5, color:T.muted }}>Row {e.row}: {e.reason}</div>
                      ))}
                      {loanParseErrors.length > 6 && <div style={{ fontSize:11.5, color:T.muted }}>...and {loanParseErrors.length - 6} more</div>}
                    </div>
                  )}
                  <div style={{ padding:"10px 14px", borderRadius:8, background:`${T.cyan}0D`, border:`1px solid ${T.cyan}1F`,
                    fontSize:12, color:T.muted, lineHeight:1.7 }}>
                    This updates existing loan deductions for <strong style={{ color:T.cyan }}>{selMonth}</strong> where an
                    employee already has one, and creates new ones otherwise. Nothing is duplicated.
                  </div>
                </>
              )}

              {loanResult && (
                <div style={{ textAlign:"center", padding:"10px 0" }}>
                  {loanResult.error ? (
                    <>
                      <AlertTriangle size={36} style={{ color:T.red, marginBottom:10 }}/>
                      <div style={{ fontSize:14, fontWeight:700, color:T.red, marginBottom:6 }}>Upload Failed</div>
                      <div style={{ fontSize:12.5, color:T.muted }}>{loanResult.error}</div>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={36} style={{ color:T.green, marginBottom:10 }}/>
                      <div style={{ fontSize:14, fontWeight:700, color:T.green, marginBottom:10 }}>Upload Complete</div>
                      <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
                        <div style={{ padding:"8px 16px", borderRadius:8, background:`${T.green}0F` }}>
                          <div style={{ fontSize:18, fontWeight:800, color:T.green }}>{loanResult.created}</div>
                          <div style={{ fontSize:10.5, color:T.muted }}>Created</div>
                        </div>
                        <div style={{ padding:"8px 16px", borderRadius:8, background:`${T.cyan}0F` }}>
                          <div style={{ fontSize:18, fontWeight:800, color:T.cyan }}>{loanResult.updated}</div>
                          <div style={{ fontSize:10.5, color:T.muted }}>Updated</div>
                        </div>
                        {loanResult.errors?.length > 0 && (
                          <div style={{ padding:"8px 16px", borderRadius:8, background:`${T.red}0F` }}>
                            <div style={{ fontSize:18, fontWeight:800, color:T.red }}>{loanResult.errors.length}</div>
                            <div style={{ fontSize:10.5, color:T.muted }}>Not Found</div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div style={{ padding:"14px 22px", borderTop:`1px solid ${T.border}`,
              display:"flex", justifyContent:"flex-end", gap:10 }}>
              {!loanResult ? (
                <>
                  <button onClick={() => setLoanModalOpen(false)} disabled={loanUploading}
                    style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.border}`,
                      background:"none", color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
                    Cancel
                  </button>
                  {loanParsed && (
                    <button onClick={submitLoanUpload} disabled={loanUploading || loanParsed.length === 0}
                      style={{ padding:"9px 22px", borderRadius:8, border:"none",
                        background: loanParsed.length === 0 ? T.toggle_bg : `linear-gradient(135deg,${T.cyan},${T.purple})`,
                        color: loanParsed.length === 0 ? T.muted : "#fff", cursor: loanParsed.length === 0 ? "not-allowed" : "pointer",
                        fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                      {loanUploading ? "Uploading…" : `Upload ${loanParsed.length} Rows`}
                    </button>
                  )}
                </>
              ) : (
                <button onClick={() => setLoanModalOpen(false)}
                  style={{ padding:"9px 22px", borderRadius:8, border:"none",
                    background:`linear-gradient(135deg,${T.green},${T.cyan})`, color:"#06101F",
                    cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}